import React, { useEffect, useState } from 'react'
import { Button, Input } from 'components/common'
import styles from './SportForm.module.scss'
import { LabeledLine } from 'components/shared'
import { convertSecondsToTimeString, convertTimeStringToSeconds } from 'utilities'
import { SPORT_POLL_STORE } from 'store'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { Option, SportFormProps, SportPoll } from './types'
import { Select } from 'components/shared/Select'

export const initialState: SportPoll = {
  startTime: '',
  endTime: '',
  resultTime: '',
  question: '',
  options: [],
  answer: '',
  projectId: 0
}

const SportForm = (props:SportFormProps) => {
  const projectId = +window.location.pathname.split('/').pop()

  initialState.projectId = +projectId

  const {
    [SPORT_POLL_STORE]: {
      createPoll,
      updatePoll
    },
    EditSportForm
  } = props

  const [formData, setFormData] = useState<SportPoll>(initialState)
  const [disableButton, setDisableButton] = useState<boolean>(true)
  const [option, setOption] = useState<Option>({
    payoutMultiple: 0,
    optionValue: ''
  })
  const [errorText, setErrorText] = useState<string>('')

  useEffect(() => {
    setDisableButton(
      !formData.startTime ||
        !formData.endTime ||
        !formData.question ||
        !formData.resultTime ||
        !formData.options.length ||
        !formData.answer
    )
  }, [formData])

  useEffect(() => {
    setFormData({
      startTime: EditSportForm.startTime && convertSecondsToTimeString(+EditSportForm.startTime) ? convertSecondsToTimeString(+EditSportForm.startTime) : '',
      endTime: EditSportForm.endTime && convertSecondsToTimeString(+EditSportForm.endTime) ? convertSecondsToTimeString(+EditSportForm.endTime) : '',
      resultTime: EditSportForm.resultTime && convertSecondsToTimeString(+EditSportForm.resultTime) ? convertSecondsToTimeString(+EditSportForm.resultTime) : '',
      question: EditSportForm.question ?? '',
      options: EditSportForm.options ?? [],
      answer: EditSportForm.answer ?? '',
      projectId: +projectId,
      ...(EditSportForm?.pollId && {
        pollId: EditSportForm.pollId ?? 0
      })
    })

    return () => {
      setFormData(initialState)
    }
  }, [EditSportForm])

  const addOption = (option:Option):void => {
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, option]
    }))
  }

  const removeOption = (newOption: Option):void => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter(option => option !== newOption)
    }))
  }

  const handleSubmit = () => {
    formData.startTime = convertTimeStringToSeconds(formData.startTime)
    formData.endTime = convertTimeStringToSeconds(formData.endTime)
    formData.resultTime = convertTimeStringToSeconds(formData.resultTime)

    if (+formData.startTime >= +formData.endTime || +formData.endTime >= +formData.resultTime) {
      setDisableButton(true)
      setErrorText('Start time should be less than or equal to end time and end time should be less than or equal to result time')
      return
    }

    if (formData.pollId) {
      updatePoll(formData)
    } else {
      // Create Poll
      createPoll(formData)
    }

    // Reset form
    setErrorText('')
    setFormData(initialState)
  }

  const answerDropdown: Array<{label : string, value : string}> = formData.options.map((option) => {
    return {
      label: option.optionValue,
      value: option.optionValue
    }
  })

  function handleDropdownOnSelect (value: string) {
    setFormData({
      ...formData,
      answer: value
    })
  }

  return (
    <div className={styles.sportForm}>
      <div className={styles.timeFields}>
        <LabeledLine label='Start Time'>
          <Input
            type='time'
            name='startTime'
            placeholder='Enter start time'
            step='1'
            value={formData.startTime}
            onChange={(startTime) =>
              setFormData((prev) => ({ ...prev, startTime }))
            }
          />
        </LabeledLine>

        <LabeledLine label='End Time'>
          <Input
            type='time'
            name='endTime'
            placeholder='Enter end time'
            step='1'
            value={formData.endTime}
            onChange={(endTime) =>
              setFormData((prev) => ({ ...prev, endTime }))
            }
          />
        </LabeledLine>

        <LabeledLine label='Result Time'>
          <Input
            type='time'
            name='resultTime'
            placeholder='Enter result time'
            step='1'
            value={formData.resultTime}
            onChange={(resultTime) =>
              setFormData((prev) => ({ ...prev, resultTime }))
            }
          />
        </LabeledLine>

        <LabeledLine label='Question'>
          <Input
            type='text'
            name='question'
            placeholder='Enter question'
            value={formData.question}
            onChange={(question) =>
              setFormData((prev) => ({ ...prev, question }))
            }
          />
        </LabeledLine>

        <div className={styles.optionsContainer}>
          <LabeledLine label='Options' />
          <div className={styles.optionsInput}>
            <Input
              type='text'
              name='options'
              placeholder='Enter option'
              value={option.optionValue}
              onChange={(optionVal) =>
                setOption({ ...option, optionValue: optionVal })
              }
              style={{ marginRight: 5 }}
            />

            <div className={styles.payoutMultiple}>
              <Input
                type='number'
                name='amountMultiple'
                title='Payout multiple'
                placeholder='Enter amount multiple'
                value={option.payoutMultiple}
                onChange={(payoutMultiple) =>
                  setOption({ ...option, payoutMultiple: +payoutMultiple })
                }
              />
              <Button
                type='add'
                onClick={() => {
                  if (option.optionValue && option.payoutMultiple) {
                    addOption(option)
                    setOption({
                      payoutMultiple: 1,
                      optionValue: ''
                    })
                  }
                }}
                style={{ marginLeft: 10 }}
                disabled={!option}
              >
              +
              </Button>
            </div>
          </div>
        </div>

        <div className={styles.optionsList}>
          {formData.options.length > 0
            ? formData.options.map((option, index) => {
              return (
                <div key={index}>
                  <LabeledLine label={`Option ${index + 1}`}>
                    <Input
                      key={index}
                      type='text'
                      name='options'
                      value={option.optionValue}
                      disabled
                      style={{ marginRight: 5 }}
                    />
                    <Input
                      key={index}
                      type='text'
                      name='payoutMultiple'
                      value={option.payoutMultiple}
                      disabled
                    />
                    <Button
                      onClick={() => removeOption(option)}
                      className={styles.removeBtn}
                    >
                        X
                    </Button>
                  </LabeledLine>
                </div>
              )
            })
            : null}
        </div>

        <LabeledLine label='Answer'>
          <Select
            options={answerDropdown}
            value={formData.answer}
            onChange={handleDropdownOnSelect}
            noNoneOption
            placeholder='Select answer'
          />
        </LabeledLine>

        {
          errorText && <p className={styles.errorText}>{errorText}</p>
        }

        <Button type='submit' onClick={handleSubmit} disabled={disableButton}>
          {EditSportForm?.pollId ? 'Update Poll' : 'Create Poll'}
        </Button>
      </div>
    </div>
  )
}

export default inject(SPORT_POLL_STORE)(observer(SportForm))
